import { useCallback, useEffect, useMemo, useState } from "react";
import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import {
  Button,
  message,
  Row,
  Col,
  Typography,
  Dropdown,
  Menu,
  Popconfirm,
  Modal,
  Form,
  Input,
  Select,
  Switch,
} from "antd";
import ListTable from "../../../common/ListTable";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ExtraContent from "../CommonComponents/ExtraContent";
import FilterComponent from "../CommonComponents/FilterComponent";
import {
  getData,
  postData,
  deleteData,
  putData,
} from "../../../apiServices/common";
import {
  CARTON_PARTITION_LIST_ENDPOINT,
  CARTON_PARTITION_ADD_ENDPOINT,
  CARTON_PARTITION_EDIT_ENDPOINT,
  CARTON_PARTITION_DELETE_ENDPOINT,
  CARTON_FLOOR_LIST_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { useSelector } from "react-redux";
import { hasPermission } from "../../../utils/functions";
import {
  CARTON_PARTITION_CREATE_PERMISSION,
  CARTON_PARTITION_EDIT_PERMISSION,
  CARTON_PARTITION_DELETE_PERMISSION,
} from "../../../routes/permissions";
import { useLocation } from "react-router";

const CartonPartitionConfig = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [floorList, setFloorList] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const [cartonPartitionForm] = Form.useForm();
  const { Option } = Select;
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllCartonPartition = useCallback(
    async (filterValues) => {
      const { name } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      const query = `${CARTON_PARTITION_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(name && { name: name }),
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        setLoading(false);
        setMetaData({
          defaultPageSize: response?.data?.data?.per_page,
          current: response?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.current_page,
          total: response?.data?.data?.total,
        });
        setTableData(response?.data?.data?.data);
      } else {
        setLoading(false);
        setFetchError("Could not load this list.");
        message.error("Error getting carton partition data");
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllCartonPartition(filterData);
    getAllCartonFloor();
  }, [refresh, filterData, getAllCartonPartition]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    cartonPartitionForm.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
  };

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const getAllCartonFloor = async (filterValues) => {
    setLoading(true);

    const query = `${CARTON_FLOOR_LIST_ENDPOINT}`;
    const payload = {
      page: 1,
      per_page: 100,
      ...filterValues,
    };

    const response = await getData(query, false, payload);
    if (response?.status === 200) {
      setLoading(false);
      setFloorList(response?.data?.data?.data);
    } else {
      setLoading(false);
      message.error("Error getting carton floor data");
    }
  };

  const deleteSelectedCartonPartition = async (id) => {
    setLoading(true);
    const response = await deleteData(`${CARTON_PARTITION_DELETE_ENDPOINT}/${id}`);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Carton Partition Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error(response?.message || "Something went wrong");
    }
  };

  const editSelectedCartonPartition = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      description: formValues?.description,
      status: formValues?.status ? "1" : "0",
      carton_floor_id: Number(formValues?.carton_floor_id),
    };

    const response = await putData(
      `${CARTON_PARTITION_EDIT_ENDPOINT}/${formValues?.id}`,
      bodyData,
    );

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Carton Partition Edited Successfully");
      refetch();
      cartonPartitionForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const createNewCartonPartition = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      description: formValues?.description,
      status: formValues?.status ? "1" : "0",
      carton_floor_id: Number(formValues?.carton_floor_id),
    };

    const response = await postData(CARTON_PARTITION_ADD_ENDPOINT, bodyData);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Carton Partition Created Successfully");
      refetch();
      cartonPartitionForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openModalForEdit = (record) => {
    setIsEdit(true);

    // Push the floor info to the floor list if it isn't already present
    const floorInfo = {
      id: Number(record?.floor_info?.id),
      name: record?.floor_info?.name,
      warehouse_info: record?.floor_info?.warehouse_info,
    };
    if (floorList && !floorList.some((item) => item.id === floorInfo.id)) {
      const floorListCopy = [...floorList];
      floorListCopy.push(floorInfo);
      setFloorList(floorListCopy);
    }

    cartonPartitionForm.setFieldsValue({
      id: record?.id,
      name: record?.name,
      description: record?.description,
      status: record?.status === "1" ? true : false,
      carton_floor_id: record?.floor_info?.id,
    });
    setIsModalOpen(true);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const tableColumns = [
    {
      title: "Carton Partition Name",
      dataIndex: ["name"],
      key: "carton_partition_name",
    },
    {
      title: "Carton Warehouse Name",
      dataIndex: ["floor_info", "warehouse_info", "name"],
      key: "carton_warehouse_name",
    },
    {
      title: "Carton Floor Name",
      dataIndex: ["floor_info", "name"],
      key: "carton_floor_name",
    },
    {
      title: "Status",
      dataIndex: ["status"],
      key: "carton_partition_status",
      render: (_, record) => {
        return <div>{record?.status === "1" ? "Active" : "Inactive"}</div>;
      },
    },
    {
      title: "Description",
      dataIndex: ["description"],
      key: "carton_partition_description",
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedCartonPartition(record?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([CARTON_PARTITION_DELETE_PERMISSION]),
                  },
                  {
                    label: (
                      <>
                        <span>
                          <EditTwoTone />
                        </span>
                        <span
                          type="button"
                          style={{ marginLeft: 5 }}
                          onClick={() => {
                            openModalForEdit(record);
                          }}
                        >
                          Edit
                        </span>
                      </>
                    ),
                    key: "2",
                    disabled: !hasPermission([CARTON_PARTITION_EDIT_PERMISSION]),
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllCartonPartition}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter, tableData, getAllCartonPartition],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([CARTON_PARTITION_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([CARTON_PARTITION_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Carton Partition Configuration</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllCartonPartition(filterData)}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={isEdit ? "Edit Carton Partition" : "Create Carton Partition"}
        onCancel={() => {
          onCloseModal();
        }}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
      >
        <Form
          form={cartonPartitionForm}
          layout="vertical"
          onFinish={isEdit ? editSelectedCartonPartition : createNewCartonPartition}
        >
          <Row gutter={6}>
            <Col span={12}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="name"
                placeholder="Carton Partition Name"
                label="Carton Partition Name"
                rules={[
                  {
                    required: true,
                    message: "Carton Partition Name Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="description"
                placeholder="Description"
                label="Description"
                rules={[
                  {
                    required: true,
                    message: "Description Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={12}>
              <Form.Item
                name="status"
                placeholder="Status"
                label="Status"
                rules={[
                  {
                    required: true,
                    message: "Status Required",
                  },
                ]}
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="carton_floor_id"
                placeholder="Carton Floor"
                label="Carton Floor"
                rules={[
                  {
                    required: true,
                    message: "Carton Floor Required",
                  },
                ]}
              >
                <Select
                  placeholder="Select Carton Floor"
                  showSearch
                  style={{ width: "100%" }}
                  onSearch={(value) => getAllCartonFloor({ name: value })}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {floorList?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {`${item.name} (${item?.warehouse_info?.name || ""})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={12}>
              <Button
                type="danger"
                style={{ width: "100%" }}
                onClick={() => {
                  onCloseModal();
                }}
              >
                Discard
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "100%" }}
                loading={loading}
              >
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default CartonPartitionConfig;
